import React, { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { SectionCard } from '../sections/SectionCard';
import { ActionDropdown } from '@shared/components/ui/action-dropdown/ActionDropdown';
import { Badge } from '@shared/components/ui/badge';
import { ScrollArea } from '@shared/components/ui/scroll-area';
import { DynamicDateCell } from '@shared/lib/tables/cells';
import { cn } from '@shared/lib/utils';
import type { EventData } from '@shared/types/events.types';
import type { TableAction } from '@shared/types/data-table';
import { getEventsConfig, type EventsEntityType } from '../../config/events';

interface EventsLogsSectionProps {
  isDetailView?: boolean;
  eventsData?: EventData[];
  entityType?: EventsEntityType;
  useCustomerLayout?: boolean;
  className?: string;
}

type BadgeVariant = 'success' | 'warning' | 'danger' | 'secondary' | 'outline';

// Map event status to a badge variant + human label.
const STATUS_BADGE: Record<EventData['status'], { variant: BadgeVariant; label: string }> = {
  succeeded: { variant: 'success', label: 'Succeeded' },
  approved: { variant: 'success', label: 'Approved' },
  verified: { variant: 'success', label: 'Verified' },
  processing: { variant: 'secondary', label: 'Processing' },
  pending: { variant: 'warning', label: 'Pending' },
  failed: { variant: 'danger', label: 'Failed' },
};

const DOT_COLOR: Record<BadgeVariant, string> = {
  success: 'bg-status-success-text',
  warning: 'bg-status-warning-text',
  danger: 'bg-status-error-text',
  secondary: 'bg-status-info-text',
  outline: 'bg-muted-foreground',
};

// ── Component ─────────────────────────────────────────────────────────────

export const EventsLogsSection = React.memo<EventsLogsSectionProps>(({
  isDetailView = false,
  eventsData,
  entityType,
  className,
}) => {
  const config = getEventsConfig(entityType);
  const events = eventsData ?? config.events;

  const rowActions = useCallback((event: EventData): TableAction<EventData>[] => [
    {
      key: 'view',
      label: 'View details',
      onClick: () => {
        toast.info(event.eventType, { description: `Request ID: ${event.requestId}` });
      },
    },
    {
      key: 'copy',
      label: 'Copy',
      onClick: () => {
        navigator.clipboard.writeText(JSON.stringify(event, null, 2));
        toast.success('Event copied to clipboard');
      },
    },
  ], []);

  const timeline = useMemo(() => (
    <ol className="flex flex-col">
      {events.map((event, index) => {
        const isLast = index === events.length - 1;
        const status = STATUS_BADGE[event.status] ?? { variant: 'outline' as const, label: event.status };
        return (
          <li key={event.id} className="flex gap-4">
            {/* Timeline rail: dot + connecting line */}
            <div className="flex flex-col items-center shrink-0 w-4 pt-1.5">
              <span
                className={cn('size-3 rounded-full ring-4 ring-background', DOT_COLOR[status.variant])}
                aria-hidden="true"
              />
              {!isLast && <span className="w-px flex-1 bg-border mt-1" />}
            </div>

            {/* Event content */}
            <div className={cn('flex items-start justify-between gap-3 min-w-0 flex-1', isLast ? 'pb-1' : 'pb-6')}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-5 text-foreground break-words">
                  {event.description}
                </p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <Badge variant={status.variant} className="text-[10px] px-2 py-0">
                    {status.label}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    <DynamicDateCell timestamp={event.timestamp} />
                  </span>
                </div>
              </div>
              <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                <ActionDropdown
                  actions={rowActions(event)}
                  item={event}
                  triggerClassName="h-5 w-5"
                  aria-label={`More actions for event ${index + 1}`}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  ), [events, rowActions]);

  return (
    <SectionCard
      title="Events and logs"
      count={events.length}
      layout="down"
      className={className}
    >
      {events.length === 0 ? (
        <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
          No events found
        </div>
      ) : isDetailView ? (
        <div className="pt-2">{timeline}</div>
      ) : (
        <ScrollArea className="max-h-80 pr-3 pt-2">{timeline}</ScrollArea>
      )}
    </SectionCard>
  );
});

EventsLogsSection.displayName = 'EventsLogsSection';
